import { inject, Injectable } from '@angular/core';
import { first, map, Observable, switchMap, timer } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '@shs-digi-care-monorepo/frontend-lib';
import { ReferralStatus } from '@shs-digi-care-monorepo/interfaces';
import type { Referral } from '@shs-digi-care-monorepo/frontend-lib';
import type { GetReferralsResponse } from './get-referrals-response.interface';

@Injectable({
    providedIn: 'root'
})
export class ReferralService {
    private readonly requestEndpoint = 'iro-nam-requests';
    private readonly httpClient = inject(HttpClient);

    private readonly pollingIntervalMs = 1000;
    private readonly maxPollingAttempts = 30;

    public getReferrals(requestId: string): Observable<Referral[]> {
        return this.httpClient
            .get<GetReferralsResponse>(`${environment.apiUrl}/${this.requestEndpoint}/${requestId}/referrals`)
            .pipe(map((response) => response.data));
    }

    public closeReferral(requestId: string, referralId: string): Observable<{ message: string }> {
        return this.httpClient
            .post<{
                message: string;
            }>(`${environment.apiUrl}/${this.requestEndpoint}/${requestId}/referrals/${referralId}/close`, {})
            .pipe(
                switchMap((response) =>
                    this.pollUntilReferralStatus(requestId, referralId, ReferralStatus.CLOSED).pipe(map(() => response))
                )
            );
    }

    public reassignReferral(
        requestId: string,
        referralId: string,
        payload: { referredOutEmail?: string; referredOutTeam?: string }
    ): Observable<{ message: string }> {
        return this.httpClient
            .post<{
                message: string;
            }>(`${environment.apiUrl}/${this.requestEndpoint}/${requestId}/referrals/${referralId}/reassign`, payload)
            .pipe(
                switchMap((response) =>
                    this.pollUntilReferralReassigned(requestId, referralId, payload).pipe(map(() => response))
                )
            );
    }

    private pollUntilReferralStatus(
        requestId: string,
        referralId: string,
        expectedStatus: ReferralStatus
    ): Observable<Referral[]> {
        let attempts = 0;
        return timer(0, this.pollingIntervalMs).pipe(
            switchMap(() => {
                attempts++;
                if (attempts > this.maxPollingAttempts) {
                    throw new Error(`Referral polling timed out after ${this.maxPollingAttempts} attempts`);
                }
                return this.getReferrals(requestId);
            }),
            first((referrals) => {
                const referral = referrals.find((r) => r.id === referralId);
                return !!referral && referral.status === expectedStatus;
            })
        );
    }

    private pollUntilReferralReassigned(
        requestId: string,
        referralId: string,
        expected: { referredOutEmail?: string; referredOutTeam?: string }
    ): Observable<Referral[]> {
        let attempts = 0;
        const expectedEmail = expected.referredOutEmail?.trim().toLowerCase();
        const expectedTeam = expected.referredOutTeam;

        return timer(0, this.pollingIntervalMs).pipe(
            switchMap(() => {
                attempts++;
                if (attempts > this.maxPollingAttempts) {
                    throw new Error(
                        `Referral reassignment polling timed out after ${this.maxPollingAttempts} attempts`
                    );
                }
                return this.getReferrals(requestId);
            }),
            first((referrals) => {
                const referral = referrals.find((r) => r.id === referralId);
                if (!referral) {
                    return false;
                }
                if (expectedTeam) {
                    return referral.referredOutTeam === expectedTeam;
                }
                if (expectedEmail) {
                    return (referral.referredOutEmail ?? '').trim().toLowerCase() === expectedEmail;
                }
                return false;
            })
        );
    }
}
